// frontend/src/features/equipment/EquipmentSetupWizard/utils/equipmentMapping.ts
import { BRANDS, MODELS, SUBCATEGORIES } from '@/data/equipmentCatalogData';
import type { Equipment, EquipmentCreate } from '@/types/equipment';
import { WIZARD_STEPS } from '../types';
import type { BrandModelValue, PendingEquipment } from '../types';

const OTHER_OPTION_VALUE = '__other__';

const WIZARD_CATEGORY_IDS = new Set(
  WIZARD_STEPS.map((step) => step.categoryId).filter((categoryId): categoryId is string =>
    Boolean(categoryId),
  ),
);

/**
 * Gets the display name from a brand/model selection.
 * Handles both catalog IDs and custom "Other" text entries.
 */
export function getDisplayName(brandModel: BrandModelValue): string {
  const { brandId, brandName, modelId, modelName } = brandModel;

  // Get brand name (catalog lookup or custom "Other" text)
  let brand: string | undefined;
  if (brandId && brandId !== OTHER_OPTION_VALUE) {
    const catalogBrand = BRANDS.find((b) => b.id === brandId);
    brand = catalogBrand?.name;
  } else if (brandName) {
    brand = brandName.trim();
  }

  // Get model name (catalog lookup or custom "Other" text)
  let model: string | undefined;
  if (modelId && modelId !== OTHER_OPTION_VALUE) {
    const catalogModel = MODELS.find((m) => m.id === modelId);
    model = catalogModel?.name;
  } else if (modelName) {
    model = modelName.trim();
  }

  // Build display name
  if (brand && model) {
    return `${brand} ${model}`;
  } else if (brand) {
    return brand;
  } else if (model) {
    return model;
  }
  return '';
}

/**
 * Gets the category ID for a subcategory ID.
 * Returns undefined if subcategory is not found.
 */
export function getCategoryForSubcategory(subcategoryId: string): string | undefined {
  const subcategory = SUBCATEGORIES.find((s) => s.id === subcategoryId);
  return subcategory?.categoryId;
}

/**
 * Maps existing equipment to a wizard category ID.
 * Prefers subcategory-based mapping and falls back to category when valid.
 */
export function getWizardCategoryForEquipment(equipment: Equipment): string | undefined {
  if (equipment.subcategory) {
    const mappedCategory = getCategoryForSubcategory(equipment.subcategory);
    if (mappedCategory && WIZARD_CATEGORY_IDS.has(mappedCategory)) {
      return mappedCategory;
    }
  }

  return WIZARD_CATEGORY_IDS.has(equipment.category) ? equipment.category : undefined;
}

/**
 * Gets the brand name from a BrandModelValue.
 */
export function getBrandName(brandModel: BrandModelValue): string | undefined {
  const { brandId, brandName } = brandModel;
  if (brandId && brandId !== OTHER_OPTION_VALUE) {
    const catalogBrand = BRANDS.find((b) => b.id === brandId);
    return catalogBrand?.name;
  }
  return brandName?.trim();
}

/**
 * Gets the model name from a BrandModelValue.
 */
export function getModelName(brandModel: BrandModelValue): string | undefined {
  const { modelId, modelName } = brandModel;
  if (modelId && modelId !== OTHER_OPTION_VALUE) {
    const catalogModel = MODELS.find((m) => m.id === modelId);
    return catalogModel?.name;
  }
  return modelName?.trim();
}

/**
 * Converts a single PendingEquipment to EquipmentCreate.
 */
export function pendingToCreate(pending: PendingEquipment): EquipmentCreate {
  const name = getDisplayName(pending.brandModel);
  const category = getCategoryForSubcategory(pending.subcategoryId);

  return {
    name,
    category: category || 'other',
    subcategory: pending.subcategoryId,
    brand: getBrandName(pending.brandModel),
    model: getModelName(pending.brandModel),
    status: 'active',
  };
}

/**
 * Converts an array of PendingEquipment to an array of EquipmentCreate.
 * Handles quantity and trackSeparately logic:
 * - quantity = 1: single item
 * - quantity > 1, trackSeparately = false: single item with (×N) in name
 * - quantity > 1, trackSeparately = true: N separate items with #1, #2, etc.
 */
export function pendingToCreateBatch(pendingEquipment: PendingEquipment[]): EquipmentCreate[] {
  const result: EquipmentCreate[] = [];

  for (const pending of pendingEquipment) {
    const baseName = getDisplayName(pending.brandModel);
    const category = getCategoryForSubcategory(pending.subcategoryId);
    const brand = getBrandName(pending.brandModel);
    const model = getModelName(pending.brandModel);

    if (pending.quantity <= 1) {
      // Single item
      result.push({
        name: baseName,
        category: category || 'other',
        subcategory: pending.subcategoryId,
        brand,
        model,
        status: 'active',
      });
    } else if (!pending.trackSeparately) {
      // Grouped item with quantity indicator
      result.push({
        name: `${baseName} (×${pending.quantity})`,
        category: category || 'other',
        subcategory: pending.subcategoryId,
        brand,
        model,
        status: 'active',
      });
    } else {
      // Separate items with numbered suffix
      for (let i = 1; i <= pending.quantity; i++) {
        result.push({
          name: `${baseName} #${i}`,
          category: category || 'other',
          subcategory: pending.subcategoryId,
          brand,
          model,
          status: 'active',
        });
      }
    }
  }

  return result;
}

/**
 * Reverse-maps brand/model strings from existing equipment back to BrandModelValue.
 * Used when editing equipment to initialize the BrandModelInput dropdowns.
 * If the brand/model matches a catalog entry, the catalog ID is used.
 * If not, the entry is treated as "Other" with the string as free text.
 */
export function reverseMapBrandModel(
  brand: string | undefined,
  model: string | undefined,
  subcategoryId: string | undefined
): BrandModelValue {
  const result: BrandModelValue = {};

  if (!brand) return result;

  // Try to find brand in catalog
  const catalogBrand = BRANDS.find(
    (b) => b.name.toLowerCase() === brand.toLowerCase()
  );

  if (catalogBrand) {
    result.brandId = catalogBrand.id;

    // Try to find model in catalog for this brand + subcategory
    if (model && subcategoryId) {
      const catalogModel = MODELS.find(
        (m) =>
          m.brandId === catalogBrand.id &&
          m.subcategoryId === subcategoryId &&
          m.name.toLowerCase() === model.toLowerCase()
      );

      if (catalogModel) {
        result.modelId = catalogModel.id;
      } else {
        result.modelId = OTHER_OPTION_VALUE;
        result.modelName = model;
      }
    } else if (model) {
      result.modelId = OTHER_OPTION_VALUE;
      result.modelName = model;
    }
  } else {
    result.brandId = OTHER_OPTION_VALUE;
    result.brandName = brand;
    if (model) {
      result.modelName = model;
    }
  }

  return result;
}
